import { Tooltip, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Checkmark12Regular, Prohibited12Regular, Shield12Regular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { vt } from '../../theme';
import { cellLabel, type CellKind, type Grid } from './model';

const CELL_W = 108;

const useStyles = makeStyles({
  scroll: { overflowX: 'auto', flexShrink: 0 },
  table: { borderCollapse: 'separate', borderSpacing: '6px', marginInline: '-6px' },
  corner: { textAlign: 'start', verticalAlign: 'bottom', paddingBottom: '4px' },
  axis: { fontSize: '10px', fontWeight: tokens.fontWeightSemibold, color: vt.fg4, letterSpacing: '0.04em', textTransform: 'uppercase' },
  colHead: {
    width: `${CELL_W}px`,
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
    color: vt.fg2,
    textAlign: 'center',
    paddingBottom: '4px',
  },
  rowHead: {
    minWidth: '118px',
    paddingRight: '12px',
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
    color: vt.fg2,
    textAlign: 'start',
  },
  td: { padding: 0 },
  cell: {
    width: `${CELL_W}px`,
    height: '48px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    paddingInline: '18px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'none',
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  allow: { color: vt.green, backgroundColor: vt.tintGreen, border: `1px solid ${vt.tintGreen}`, ':hover': { border: `1px solid ${vt.green}` } },
  partial: {
    color: vt.orange,
    backgroundColor: vt.tintOrange,
    border: `1px solid ${vt.tintOrange}`,
    ':hover': { border: `1px solid ${vt.orange}` },
  },
  deny: { color: vt.red, backgroundColor: vt.tintRed, border: `1px solid ${vt.tintRed}`, ':hover': { border: `1px solid ${vt.red}` } },
  within: {
    justifyContent: 'center',
    color: vt.fg4,
    border: `1px dashed ${vt.surface2}`,
    fontWeight: tokens.fontWeightRegular,
    ':hover': { border: `1px dashed ${vt.fg4}` },
  },
  icon: { display: 'flex', flexShrink: 0 },
  note: { margin: 0, fontSize: '11px', color: vt.fg3 },
});

const ICON: Record<Exclude<CellKind, 'within'>, ReactNode> = {
  allow: <Checkmark12Regular />,
  partial: <Shield12Regular />,
  deny: <Prohibited12Regular />,
};

/**
 * Network-to-network reachability (mockup 04): rows are sources, columns destinations, because
 * policy is directional. Each cell opens the Firewall, where the rules that decide it live.
 */
export function Matrix({ grid }: { grid: Grid }) {
  const s = useStyles();
  return (
    <>
      <div className={s.scroll}>
        <table className={s.table} aria-label="Reachability">
          <thead>
            <tr>
              <th className={s.corner} scope="col">
                <span className={s.axis}>from ↓ · to →</span>
              </th>
              {grid.columns.map((c) => (
                <th key={c.id} className={s.colHead} scope="col">
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((r) => (
              <tr key={r.id} data-source={r.id}>
                <th className={s.rowHead} scope="row">
                  {r.name}
                </th>
                {r.cells.map((c) => (
                  <td key={c.destination} className={s.td}>
                    <Tooltip content={c.title} relationship="description" withArrow>
                      <Link
                        to="/firewall"
                        className={mergeClasses(s.cell, s[c.kind])}
                        data-cell={`${c.source}>${c.destination}`}
                        data-reach={c.reach}
                        data-rules={c.rules.join(' ')}
                        aria-label={`${r.name} to ${grid.columns.find((x) => x.id === c.destination)?.name ?? c.destination}: ${cellLabel(c)}`}
                      >
                        {c.kind !== 'within' && (
                          <span className={s.icon} aria-hidden>
                            {ICON[c.kind]}
                          </span>
                        )}
                        {cellLabel(c)}
                      </Link>
                    </Tooltip>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* The colours cannot say what happens when nothing matches; this line does. */}
      <p className={s.note}>Anything not allowed is denied. The relay enforces this — it is the only path between any two devices.</p>
    </>
  );
}
